<script setup lang="ts">
import type { PropType } from 'vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaFontSize, gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** Outcome name, e.g. "Low risk". Omit for the chip-only (editing) form. */
  name: {
    type: String,
    default: undefined,
  },
  /**
   * Ground of the card. `tinted` is for a card sitting on a white panel — the
   * evaluation's options, where the panel is the plain surface and each option
   * is the raised block on it.
   */
  tone: {
    type: String as PropType<'plain' | 'tinted'>,
    default: 'plain',
  },
});

const border = gaiaNeutral.borderCard;
const surface = gaiaNeutral.surface;
const soft = gaiaNeutral.surfaceSoft;
const text = gaiaNeutral.text;
const textSize = gaiaFontSize.meta;
</script>

<template>
  <!-- One outcome of an evaluation, or one position of a clause (Playbooks
       mock). #chip carries the risk pill or clause tier, the default slot the
       description (or a textarea while editing), #footer the actions.
       The footer is a continuation of the same padded box, not a band of its
       own: whatever is slotted in owns the rule above it (GaiaRemovableList's
       `flush` draws one), so the card must not draw a second. -->
  <div class="gaia-outcome-card" :class="props.tone">
    <n-flex vertical :size="7" :style="{ padding: $slots.footer ? '11px 12px 0' : '11px 12px' }">
      <n-flex v-if="props.name" align="center" :size="8" :wrap="false">
        <gaia-text truncate size="dense" weight="bold" tone="ink">{{ props.name }}</gaia-text>
        <span v-if="$slots.chip" :style="{ marginLeft: 'auto', flex: 'none' }"
          ><slot name="chip"
        /></span>
      </n-flex>
      <n-flex v-else-if="$slots.chip" :size="0"><slot name="chip" /></n-flex>
      <div class="text"><slot /></div>
    </n-flex>
    <div v-if="$slots.footer" :style="{ padding: '0 12px 11px' }">
      <slot name="footer" />
    </div>
  </div>
</template>

<style scoped>
.gaia-outcome-card {
  border: 1px solid v-bind(border);
  border-radius: 10px;
  overflow: hidden;
}

.plain {
  background: v-bind(surface);
}

.tinted {
  background: v-bind(soft);
}

.text {
  font-size: v-bind(textSize);
  line-height: 1.6;
  color: v-bind(text);
  text-wrap: pretty;
}
</style>
